import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Accordion, Modal } from "react-bootstrap";

import Table from "../../../../../../components/table";
import Pagination from "../../../../../../components/Pagination";
import usePagination from "../../../../../../utils/usePagination";
import api from "../../../../../../api/api";

import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import Tooltip from "@mui/material/Tooltip";

import ViewPerformaInvoice from "../../../../../../pages/Billing&Invoice/Invoicing/ViewPerformaInvoice";
import Taxinvoice from "../../../../../../pages/Billing&Invoice/Invoicing/Taxinvoice";

const SalesSection = () => {
  const { id } = useParams();

  // =========================================================
  // SALE INVOICES
  // =========================================================

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(null);

  // =========================================================
  // MODAL
  // =========================================================

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // PDF (view mode)
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");

  // =========================================================
  // FETCH SALE INVOICES
  // =========================================================

  const fetchSaleInvoices = async () => {
    if (!id || id === "new") {
      setInvoices([]);
      return;
    }

    try {
      setLoading(true);

      const response = await api.get(`/sale-invoices/by-enquiry/${id}`);

      const entries =
        response.data?.entries ||
        response.data?.invoices ||
        response.data?.data ||
        [];

      setInvoices(Array.isArray(entries) ? entries : []);
    } catch (error) {
      console.error("Failed to fetch sale invoices:", error);

      console.error("API response:", error?.response?.data);

      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSaleInvoices();
  }, [id]);

  // =========================================================
  // SEARCH
  // =========================================================

  const filteredInvoices = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    if (!searchValue) {
      return invoices;
    }

    return invoices.filter((invoice) => {
      const invoiceNumber = invoice.inv_number?.toString().toLowerCase() || "";

      const invoiceType = invoice.invoice_type?.toString().toLowerCase() || "";

      const invoiceDate = invoice.invoice_date?.toString().toLowerCase() || "";

      return (
        invoiceNumber.includes(searchValue) ||
        invoiceType.includes(searchValue) ||
        invoiceDate.includes(searchValue)
      );
    });
  }, [invoices, search]);

  // =========================================================
  // PAGINATION
  // =========================================================

  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    goToPage,
    handleItemsPerPageChange,
  } = usePagination(filteredInvoices, 5);

  // =========================================================
  // DATE FORMATTERS
  // =========================================================

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleDateString("en-GB");
  };

  const formatDateTime = (value) => {
    if (!value) {
      return "-";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString("en-GB");
  };

  // =========================================================
  // VIEW
  // =========================================================

  const handleView = (invoice) => {
    if (!invoice) {
      return;
    }

    const invoiceId =
      invoice.sale_invoice_id || invoice.id || invoice.invoice_id;

    if (!invoiceId) {
      console.error("Sale invoice ID not found:", invoice);
      alert("Invoice ID not found.");
      return;
    }

    setSelectedInvoice({
      ...invoice,
      sale_invoice_id: invoiceId,
    });

    setModalMode("view");
    setShowModal(true);
  };
  // =========================================================
  // EDIT
  // =========================================================

  const handleEdit = (invoice) => {
    if (!invoice) {
      return;
    }

    const invoiceId =
      invoice.sale_invoice_id || invoice.id || invoice.invoice_id;

    if (!invoiceId) {
      console.error("Sale invoice ID not found:", invoice);

      alert("Invoice ID not found.");
      return;
    }

    console.log("Opening Edit Invoice:", invoiceId);

    setSelectedInvoice({
      ...invoice,
      sale_invoice_id: invoiceId,
    });

    setModalMode("edit");
    setShowModal(true);
  };

  // =========================================================
  // CLOSE MODAL
  // =========================================================

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInvoice(null);
    setModalMode(null);
  };

  // =========================================================
  // DOWNLOAD FROM TABLE
  // =========================================================

  const handleDownload = async (invoice) => {
    const invoiceId =
      invoice?.sale_invoice_id || invoice?.id || invoice?.invoice_id;

    if (!invoiceId) {
      alert("Invoice ID not found.");
      return;
    }

    if (!invoice?.pdf_url) {
      alert("PDF not available yet. Please generate the invoice first.");
      return;
    }

    try {
      setDownloadLoading(invoiceId);

      const response = await api.get(
        `/download-document/performa/${invoiceId}`,
        {
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = url;

      link.download = `Performa_Invoice_${invoice.inv_number || invoiceId}.pdf`;

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);

      console.error("Download API response:", error?.response?.data);

      alert("Failed to download the invoice PDF. Please try again.");
    } finally {
      setDownloadLoading(null);
    }
  };

  // =========================================================
  // TABLE COLUMNS
  // =========================================================

  const columns = [
    {
      label: "Invoice Number",

      render: (invoice) => <p>{invoice.inv_number || "—"}</p>,
    },

    {
      label: "Invoice Type",

      render: (invoice) => {
        const type = invoice.invoice_type || "";

        return (
          <span
            className={`badge ${
              type === "performa"
                ? "bg-secondary"
                : type === "final"
                  ? "bg-warning text-dark"
                  : "bg-light text-dark"
            }`}
          >
            {type ? type.charAt(0).toUpperCase() + type.slice(1) : "—"}
          </span>
        );
      },
    },

    {
      label: "Invoice Date",

      render: (invoice) => formatDate(invoice.invoice_date),
    },

    {
      label: "Created At",

      render: (invoice) => formatDateTime(invoice.created_at),
    },

    {
      label: "Actions",

      style: {
        width: "180px",
        textAlign: "center",
      },

      tdStyle: {
        textAlign: "center",
      },

      render: (invoice) => {
        const invoiceId =
          invoice?.sale_invoice_id || invoice?.id || invoice?.invoice_id;

        return (
          <div className="d-flex justify-content-center gap-3">
            {/* VIEW */}

            <Tooltip title="View" arrow placement="top">
              <VisibilityIcon
                style={{
                  cursor: "pointer",
                  fontSize: 21,
                  color: "#8393a2",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(invoice);
                }}
              />
            </Tooltip>

            {/* EDIT */}

            <Tooltip title="Edit" arrow placement="top">
              <EditIcon
                style={{
                  cursor: "pointer",
                  fontSize: 21,
                  color: "#8393a2",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(invoice);
                }}
              />
            </Tooltip>

            {/* DOWNLOAD */}

            <Tooltip title="Download" arrow placement="top">
              <DownloadIcon
                style={{
                  cursor:
                    downloadLoading === invoiceId ? "not-allowed" : "pointer",

                  fontSize: 21,

                  color: downloadLoading === invoiceId ? "#c0c0c0" : "#8393a2",
                }}
                onClick={(e) => {
                  e.stopPropagation();

                  if (downloadLoading !== invoiceId) {
                    handleDownload(invoice);
                  }
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
  ];

  // =========================================================
  // RETURN
  // =========================================================

  return (
    <>
      <Accordion.Item eventKey="0">
        <Accordion.Header>
          <span className="fw-bold text-black">Sale Invoices</span>
        </Accordion.Header>

        <Accordion.Body>
          {/* SEARCH */}

          <div className="mb-3 position-relative">
            <input
              type="search"
              className="form-control ps-5"
              placeholder="Search by Invoice Number or Type"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                goToPage(1);
              }}
            />

            <SearchIcon
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#6b7280",
                width: "17px",
                height: "17px",
              }}
            />
          </div>

          {/* TABLE */}

          <Table
            columns={columns}
            data={currentItems}
            loading={loading}
            emptyMessage="No sale invoices found."
            itemsPerPage={itemsPerPage}
            pagination={
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                totalItems={totalItems}
              />
            }
          />
        </Accordion.Body>
      </Accordion.Item>

      {/* =====================================================
          VIEW / EDIT MODAL
      ===================================================== */}

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="xl"
        centered
        scrollable
        backdrop="static"
        keyboard={false}
        dialogClassName="sale-invoice-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "view" ? "View Sale Invoice" : "Edit Sale Invoice"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="sale-invoice-modal-body">
          {/* =================================================
              VIEW (PDF)
          ================================================= */}

          {modalMode === "view" && selectedInvoice && (
            <div className="sale-invoice-pdf-wrap">
              <div className="d-flex justify-content-end mb-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleDownload(selectedInvoice)}
                >
                  Download
                </button>
              </div>

              <ViewPerformaInvoice
                invoiceId={selectedInvoice.sale_invoice_id}
                isModal={true}
              />
            </div>
          )}

          {/* =================================================
              EDIT
          ================================================= */}

          {modalMode === "edit" && selectedInvoice && (
            <div className="sale-invoice-edit-wrap">
              <Taxinvoice
                invoiceId={selectedInvoice.sale_invoice_id}
                saleInvoiceId={selectedInvoice.sale_invoice_id}
                enquiryId={selectedInvoice.enq_id || id}
                isModal={true}
                onClose={handleCloseModal}
                onSaved={() => {
                  handleCloseModal();
                  fetchSaleInvoices();
                }}
              />
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* =====================================================
          MODAL CSS
      ===================================================== */}

      <style>
        {`
          /* =================================================
             MODAL SIZE
          ================================================= */

          .sale-invoice-modal {
            max-width: 1250px !important;
            width: 96%;
          }

          .sale-invoice-modal .modal-content {
            border: none;
            border-radius: 10px;
            overflow: hidden;
          }

          /* =================================================
             MODAL HEADER
          ================================================= */

          .sale-invoice-modal .modal-header {
            background: #ffffff;
            border-bottom: 1px solid #e5e7eb;
            padding: 14px 20px;
          }

          .sale-invoice-modal .modal-title {
            font-size: 18px;
            font-weight: 600;
            color: #212529;
          }
          /* =================================================
             MODAL BODY
          ================================================= */

          .sale-invoice-modal-body {
            padding: 20px !important;
            background: #ffffff;
            max-height: calc(100vh - 100px);
            overflow-y: auto;
            overflow-x: hidden;
          }

                  /* =================================================
             INVOICE (HTML) VIEW
          ================================================= */

          .sale-invoice-pdf-wrap {
            width: 100%;
            background: #ffffff;
          }

          /* Hide ViewPerformaInvoice's own Back/Download action bar
             (the first .mb-3 inside .performa-invoice-page) in the modal */
          .sale-invoice-pdf-wrap .performa-invoice-page > .mb-3:first-child {
            display: none !important;
          }

          /* Make invoice cells white so they blend with the modal */
          .sale-invoice-pdf-wrap .invoice,
          .sale-invoice-pdf-wrap .invoice .invoice-table,
          .sale-invoice-pdf-wrap .invoice .invoice-table th,
          .sale-invoice-pdf-wrap .invoice .invoice-table td {
            background-color: #ffffff !important;
          }

          /* =================================================
             EDIT WRAP
          ================================================= */

          .sale-invoice-edit-wrap {
            width: 100%;
            max-width: 100%;
          }

          /* =================================================
             HIDE BREADCRUMBS INSIDE MODAL
          ================================================= */

          .sale-invoice-modal-body .breadcrumb,
          .sale-invoice-modal-body .breadcrumb-wrapper,
          .sale-invoice-modal-body .page-breadcrumb,
          .sale-invoice-modal-body .page-header,
          .sale-invoice-modal-body .content-header {
            display: none !important;
          }

          /*
             Breadcrumbs component can also render inside
             a container. Hide common breadcrumb wrappers.
          */

          .sale-invoice-modal-body nav[aria-label="breadcrumb"] {
            display: none !important;
          }

          .sale-invoice-modal-body
          nav[aria-label="Breadcrumb"] {
            display: none !important;
          }

          /* =================================================
             INVOICE PAGE
          ================================================= */

          .sale-invoice-modal-body .performa-invoice-page {
            width: 100%;
            max-width: none;
            margin: 0;
            padding: 20px;
            background: #ffffff;
          }

          /* =================================================
             INVOICE
          ================================================= */

          .sale-invoice-modal-body #invoice {
            width: 100%;
            margin: 0 auto;
          }

          /* =================================================
             ACTION BUTTONS
          ================================================= */

          .sale-invoice-modal-body .invoice-view-actions {
            margin-bottom: 15px;
          }

          /* =================================================
             TABLE
          ================================================= */

          .sale-invoice-modal-body .invoice-table {
            width: 100%;
          }

          /*
             Keep invoice readable.
             Horizontal scrolling happens inside modal.
          */

           .sale-invoice-modal-body table {
            min-width: 0;
            width: 100%;
            max-width: 100%;
          }

          /* =================================================
             SCROLLBAR
          ================================================= */

          .sale-invoice-modal-body::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          .sale-invoice-modal-body::-webkit-scrollbar-thumb {
            background: #b8b8b8;
            border-radius: 10px;
          }

          .sale-invoice-modal-body::-webkit-scrollbar-track {
            background: #f1f1f1;
          }

          /* =================================================
             MOBILE
          ================================================= */

          @media (max-width: 768px) {

            .sale-invoice-modal {
              width: 100%;
              max-width: 100% !important;
              margin: 0;
            }

            .sale-invoice-modal-body
            .performa-invoice-page {
              padding: 10px;
            }

          }
        `}
      </style>
    </>
  );
};
export default SalesSection;
